import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import withApi from '../../higherOrderComponents/withApi';
import { naturalCompare } from '../../../common/utils';
import { computeRules, gridShape, hallOf, splitCommonParts } from './courtLayoutRules';

/**
 * The "Layout view" of a product's integrated resources, read only: for each
 * court the centre sends, the ways it is sold and how each way divides it. It
 * draws the configuration, not the bookings: nothing here is live availability.
 */

const MUTED_STYLE = { color: 'grey', fontSize: '0.85rem' };
const CELL_WIDTH = 96;
const CELL_HEIGHT = 30;

// One tint per product: fill, border, text.
const PALETTE = [
  ['#dbeafe', '#3b82f6', '#1e3a8a'],
  ['#dcfce7', '#22c55e', '#14532d'],
  ['#ffedd5', '#f97316', '#7c2d12'],
  ['#ede9fe', '#8b5cf6', '#4c1d95'],
  ['#fce7f3', '#ec4899', '#831843'],
  ['#ccfbf1', '#14b8a6', '#134e4a'],
  ['#fef9c3', '#eab308', '#713f12'],
  ['#fee2e2', '#ef4444', '#7f1d1d']
].map(([fill, border, text]) => ({ fill, border, text }));

const hash = text => [...text.toLowerCase()].reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) >>> 0, 7);

// The same product always gets the same tint; two products on the page share one
// only when there are more products than tints.
const colorsFor = products => {
  const colors = new Map();
  const taken = new Set();
  [...products].sort(naturalCompare).forEach(product => {
    let index = hash(product) % PALETTE.length;
    for (let tries = 0; tries < PALETTE.length && taken.has(index); tries++) {
      index = (index + 1) % PALETTE.length;
    }
    taken.add(index);
    colors.set(product, PALETTE[index]);
  });
  return colors;
};

const percent = units => `${Math.round(100 / units)}%`;
const distinct = values => [...new Set(values)];

/** A court as the view draws it, from one integrated resource row. */
export const courtOf = row => {
  const name = row.resourceGroupName || row.match || row.externalId || '';
  const splits = row.splits || [];
  const offers = splits
    .filter(split => (split.resources || []).length > 0)
    .map(split => ({
      key: split.id,
      // One per resource: a split can sell its courts through different products.
      products: split.resources.map(resource => resource.product || 'None'),
      product: distinct(split.resources.map(resource => resource.product || 'None')).join(' / '),
      names: split.resources.map(resource => resource.name),
      units: split.resources.length
    }));

  const court = { row, name, hall: hallOf(name), offers };
  if (!row.resourceGroupId) return { ...court, state: 'centreTitle' };
  if (!row.resourceGroupName) return { ...court, state: 'missingGroup' };
  if (offers.length === 0) return { ...court, state: 'notConfigured' };

  return { ...court, state: 'configured', rules: computeRules(offers) };
};

/**
 * A court drawn as a grid of `cells`, with `blocks` `{ offer, i, k, name }` laid
 * on it. A block that wraps to the next row is drawn in pieces, named once.
 * Names are often long and alike: what they share is written once above the
 * grid, and each block shows only what sets it apart.
 */
const Field = ({ cells, blocks, offers, colors, width }) => {
  const [rows, cols] = gridShape(cells);
  const pieces = blocks.flatMap(block => {
    if (block.k % cols === 0 && block.i % cols === 0) {
      return [{ block, row: block.i / cols, col: 0, rowSpan: block.k / cols, colSpan: cols, first: true }];
    }
    const out = [];
    for (let cell = block.i; cell < block.i + block.k;) {
      const col = cell % cols;
      const span = Math.min(cols - col, block.i + block.k - cell);
      out.push({ block, row: Math.floor(cell / cols), col, rowSpan: 1, colSpan: span, first: cell === block.i });
      cell += span;
    }
    return out;
  });
  const { prefix, suffix, parts } = splitCommonParts(blocks.map(block => block.name));
  const shared = [prefix, suffix].some(Boolean) ? [prefix, '…', suffix].filter(Boolean).join(' ') : null;

  return (
    <>
      <div
        title={shared || undefined}
        style={{
          ...MUTED_STYLE,
          height: '1.3em',
          width: width || cols * CELL_WIDTH,
          maxWidth: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {shared}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, ${CELL_HEIGHT}px)`,
          gap: 2,
          width: width || cols * CELL_WIDTH,
          maxWidth: '100%'
        }}
      >
        {Array.from({ length: cells }, (_, cell) => (
          <div
            key={`cell-${cell}`}
            style={{
              gridRow: Math.floor(cell / cols) + 1,
              gridColumn: (cell % cols) + 1,
              border: '1px dashed #bdbdbd',
              borderRadius: 3
            }}
          />
        ))}
        {pieces.map(({ block, row, col, rowSpan, colSpan, first }) => {
          const color = colors.get(offers[block.offer].products[block.i / block.k]);
          return (
            <div
              key={`${block.i}-${row}-${col}`}
              title={`${block.name} · ${offers[block.offer].products[block.i / block.k]}`}
              style={{
                gridRow: `${row + 1} / span ${rowSpan}`,
                gridColumn: `${col + 1} / span ${colSpan}`,
                background: color.fill,
                border: `1px solid ${color.border}`,
                color: color.text,
                borderRadius: 3,
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                padding: '0 3px'
              }}
            >
              {first ? parts[blocks.indexOf(block)] : ''}
            </div>
          );
        })}
      </div>
    </>
  );
};

Field.propTypes = {
  cells: PropTypes.number.isRequired,
  blocks: PropTypes.array.isRequired,
  offers: PropTypes.array.isRequired,
  colors: PropTypes.object.isRequired,
  width: PropTypes.number
};

// The court sold one way: every part of the offer, in order.
const offerBlocks = (offers, j, k) => offers[j].names.map((name, part) => ({ offer: j, i: part * k, k, name }));

const Captioned = ({ caption, children }) => (
  <div style={{ display: 'inline-block', verticalAlign: 'top', margin: '0 16px 12px 0' }}>
    {children}
    <div style={{ ...MUTED_STYLE, marginTop: 4 }}>{caption}</div>
  </div>
);

Captioned.propTypes = { caption: PropTypes.node.isRequired, children: PropTypes.node.isRequired };

const CARD_STYLE = { border: '1px solid #d6d6d6', borderRadius: 4, padding: 10, background: '#fff' };

const CourtName = ({ court }) => (
  <strong>
    {court.row.resourceGroupName
      ? <Link to={`/ResourceGroup/${court.row.resourceGroupId}`}>{court.name}</Link>
      : court.name}
  </strong>
);

CourtName.propTypes = { court: PropTypes.object.isRequired };

const UNCONFIGURED_TEXT = {
  centreTitle: 'No resource group: shown with the title the centre sends.',
  missingGroup: 'Resource group deleted or missing.',
  notConfigured: 'Not configured: the resource group sells nothing.'
};

const CourtCard = ({ court, colors }) => {
  if (court.state !== 'configured') {
    return (
      <div style={{ ...CARD_STYLE, background: '#f1f1f1', color: 'grey' }}>
        <CourtName court={court} />
        <div style={{ fontSize: '0.85rem', marginTop: 6 }}>{UNCONFIGURED_TEXT[court.state]}</div>
      </div>
    );
  }

  const { offers, rules } = court;

  // Every way sells the whole court: one block each, side by side.
  if (rules.cells === 1) {
    return (
      <div style={CARD_STYLE}>
        <CourtName court={court} />
        <div style={{ marginTop: 8 }}>
          {offers.map((offer, j) => (
            <Captioned key={offer.key} caption={offer.product}>
              <Field cells={1} blocks={offerBlocks(offers, j, 1)} offers={offers} colors={colors} width={160} />
            </Captioned>
          ))}
        </div>
        <div style={MUTED_STYLE}>{offers.length === 1 ? 'Single mode' : 'Mutually exclusive'}</div>
      </div>
    );
  }

  const [rows, cols] = gridShape(rules.cells);

  return (
    <div style={{ ...CARD_STYLE, gridColumn: '1 / -1' }}>
      <CourtName court={court} />
      <span style={{ ...MUTED_STYLE, marginLeft: 12 }}>
        grid {rows}×{cols} · {offers.length} mode{offers.length === 1 ? '' : 's'}
      </span>

      <div style={{ marginTop: 10, fontWeight: 'bold' }}>Sold as</div>
      <div style={{ marginTop: 6 }}>
        {offers.map((offer, j) => (
          <Captioned key={offer.key} caption={`${offer.product} · ${offer.units} × ${percent(offer.units)}`}>
            <Field cells={rules.cells} blocks={offerBlocks(offers, j, rules.ks[j])} offers={offers} colors={colors} />
          </Captioned>
        ))}
      </div>

    </div>
  );
};

CourtCard.propTypes = { court: PropTypes.object.isRequired, colors: PropTypes.object.isRequired };

const IntegratedResourcesLayout = ({ api, productId }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.integrations.getIntegratedResources(productId)
      .then(loaded => setRows(loaded || []))
      .catch(failure => setError(failure?.message || 'unknown error'));
  }, [api, productId]);

  const courts = useMemo(() => (rows || []).map(courtOf), [rows]);

  const halls = useMemo(() => {
    const byHall = new Map();
    courts.forEach(court => {
      byHall.set(court.hall, [...(byHall.get(court.hall) || []), court]);
    });
    return [...byHall.entries()]
      .map(([name, members]) => ({ name, courts: members.sort((a, b) => naturalCompare(a.name, b.name)) }))
      // Courts whose name tells no hall come last, outside any hall.
      .sort((a, b) => (a.name === null) - (b.name === null) || naturalCompare(a.name, b.name));
  }, [courts]);

  const colors = useMemo(
    () => colorsFor(distinct(courts.flatMap(court => court.offers.flatMap(offer => offer.products)))),
    [courts]
  );

  if (error) return <p style={{ color: '#c0392b' }}>Unable to load the integrated resources: {error}</p>;
  if (rows === null) return <>Loading...</>;

  return (
    <>
      <h3>Integrated Resources ({rows.length})</h3>
      <p style={MUTED_STYLE}>
        How each court is sold, read only. Every split of a court's resource group is one way of selling it,
        divided into as many parts as it has resources. The position of the parts is drawn from the order of
        the resources; the crawl does not know where they lie.
      </p>

      {colors.size > 0 && (
        <div style={{ marginBottom: 16 }}>
          {[...colors.entries()].map(([product, color]) => (
            <span key={product} style={{ marginRight: 16, whiteSpace: 'nowrap' }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  verticalAlign: 'middle',
                  marginRight: 6,
                  background: color.fill,
                  border: `1px solid ${color.border}`,
                  borderRadius: 3
                }}
              />
              {product}
            </span>
          ))}
        </div>
      )}

      {rows.length === 0 && <p>No integrated resource yet.</p>}

      {halls.map(hall => {
        const cards = (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
            {hall.courts.map(court => <CourtCard key={court.row.id} court={court} colors={colors} />)}
          </div>
        );

        return hall.name === null ? (
          <div key="no-hall" style={{ marginBottom: 16 }}>{cards}</div>
        ) : (
          <div key={hall.name} style={{ border: '1px dashed #9e9e9e', borderRadius: 6, padding: 12, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <strong>{hall.name}</strong>
              <span style={MUTED_STYLE}>{hall.courts.length} court{hall.courts.length === 1 ? '' : 's'}</span>
            </div>
            {cards}
          </div>
        );
      })}
    </>
  );
};

IntegratedResourcesLayout.propTypes = {
  api: PropTypes.object.isRequired,
  productId: PropTypes.string.isRequired
};

export default withApi(IntegratedResourcesLayout);
